import {
  attachedModelSpecs,
  mergeModelSpecMaps
} from 'components/equipment-page/combination-model-specs';
import type { DuctedPanelSelection } from 'components/equipment-page/ducted-system-detail-panel';
import type {
  DuctedCompatibilityEntry,
  DuctedRepresentativeEntry,
  DuctedResultSet
} from 'features/ducted-compatibility';
import {
  DuctedComparedSystem,
  findComparedSystemForCard
} from './ducted-compare-identity';
import { formatKeptSystemTypeLabel } from './kept-system-type-annotation';

export type DuctedCompareSelectRequest = {
  systemId: string;
  nonce: number;
};

export type DuctedRailSubSection = {
  resultSet: DuctedResultSet;
  entry: DuctedCompatibilityEntry | undefined;
  representativeEntries: Array<{
    systemType: { code: string; label: string };
    representative: DuctedRepresentativeEntry | undefined;
  }>;
  tail: Record<string, unknown>[];
};

export type DuctedRailRankGroupCardRef = {
  resultSet: DuctedResultSet;
  entry: DuctedCompatibilityEntry;
  systemType: { code: string; label: string };
  representative: DuctedRepresentativeEntry | undefined;
};

export type DuctedRailRankGroupTailRef = {
  resultSet: DuctedResultSet;
  entry: DuctedCompatibilityEntry;
  combinations: Record<string, unknown>[];
};

export type DuctedRailRankGroup = {
  key: string;
  cards: DuctedRailRankGroupCardRef[];
  tails: DuctedRailRankGroupTailRef[];
};

export type DuctedRailSection = {
  system: { uniqueId: string };
  keptComponentTypes: string[];
  subSections: DuctedRailSubSection[];
  rankGroups: DuctedRailRankGroup[];
};

export type ComparedSelectionMatch = {
  selection: DuctedPanelSelection;
  expandKey: string | null;
};

export type RankGroupTailSelection = {
  resultSet: DuctedResultSet;
  entry: DuctedCompatibilityEntry;
  combination: Record<string, unknown>;
  selection: DuctedPanelSelection;
};

type TailSelectionParams = {
  systemUniqueId: string;
  resultSet: DuctedResultSet;
  entry: DuctedCompatibilityEntry;
  combination: Record<string, unknown>;
  fallbackTitle: string;
  keptComponentTypes: string[];
  tailCodeByLabel: Map<string, string>;
};

export const shownRankGroupCardCount = (
  group: Pick<DuctedRailRankGroup, 'cards'>
): number =>
  group.cards.filter(
    ({ representative }) => representative?.combination != null
  ).length;

export const facetCodeByLabel = (
  systemTypes: Array<{ code: string; label: string }>
): Map<string, string> => {
  const byLabel = new Map<string, string>();
  systemTypes.forEach((systemType) => {
    if (!byLabel.has(systemType.label)) {
      byLabel.set(systemType.label, systemType.code);
    }
  });
  return byLabel;
};

export const representativeSelection = (
  systemUniqueId: string,
  resultSet: DuctedResultSet,
  systemType: { code: string; label: string },
  representative: DuctedRepresentativeEntry,
  combination: Record<string, unknown>,
  keptComponentTypes: string[],
  modelSpecs: Record<string, Record<string, unknown>> = {}
): DuctedPanelSelection => {
  const keptPairing = resultSet === 'kept';
  const rawTitle = (combination.SystemTypeLabel as string) ?? systemType.label;
  const title = keptPairing
    ? formatKeptSystemTypeLabel(rawTitle, systemType.code, keptComponentTypes)
    : rawTitle;
  return {
    cardId: `${systemUniqueId}:${resultSet}:rep:${systemType.code}`,
    systemUniqueId,
    title,
    ...(keptPairing ? { keptPairing, displayTypeLabel: title } : {}),
    typeCode: systemType.code,
    combination,
    productData: representative.productData,
    accessoryData: representative.accessoryData,
    ...attachedModelSpecs(modelSpecs)
  };
};

export const tailSelection = ({
  systemUniqueId,
  resultSet,
  entry,
  combination,
  fallbackTitle,
  keptComponentTypes,
  tailCodeByLabel
}: TailSelectionParams): DuctedPanelSelection => {
  const keptPairing = resultSet === 'kept';
  const rawTypeLabel = (combination.SystemTypeLabel as string) ?? '';
  const tailCode = tailCodeByLabel.get(rawTypeLabel);
  const typeLabel =
    keptPairing && tailCode
      ? formatKeptSystemTypeLabel(rawTypeLabel, tailCode, keptComponentTypes)
      : rawTypeLabel;
  return {
    cardId: `${systemUniqueId}:${resultSet}:${combination.ID}`,
    systemUniqueId,
    title: typeLabel || fallbackTitle,
    ...(keptPairing ? { keptPairing, displayTypeLabel: typeLabel } : {}),
    ...(tailCode ? { typeCode: tailCode } : {}),
    combination,
    productData: entry.productData,
    accessoryData: entry.accessoryData,
    ...attachedModelSpecs(entry.modelSpecs)
  };
};

export const rankGroupCardSelection = (
  section: Pick<DuctedRailSection, 'system' | 'keptComponentTypes'>,
  card: DuctedRailRankGroupCardRef
): DuctedPanelSelection | null => {
  const { representative } = card;
  if (representative?.combination == null) return null;
  return representativeSelection(
    section.system.uniqueId,
    card.resultSet,
    card.systemType,
    representative,
    representative.combination,
    section.keptComponentTypes,
    mergeModelSpecMaps([card.entry.modelSpecs, representative.modelSpecs])
  );
};

export const rankGroupTailSelections = (
  section: Pick<DuctedRailSection, 'system' | 'keptComponentTypes'>,
  group: DuctedRailRankGroup
): RankGroupTailSelection[] => {
  const cardCount = shownRankGroupCardCount(group);
  const rows = group.tails.flatMap((tail) => {
    const tailCodeByLabel = facetCodeByLabel(tail.entry.systemTypes);
    return tail.combinations.map((combination) => ({
      tail,
      combination,
      tailCodeByLabel
    }));
  });
  return rows.map(({ tail, combination, tailCodeByLabel }, index) => ({
    resultSet: tail.resultSet,
    entry: tail.entry,
    combination,
    selection: tailSelection({
      systemUniqueId: section.system.uniqueId,
      resultSet: tail.resultSet,
      entry: tail.entry,
      combination,
      fallbackTitle: `System ${cardCount + index + 1}`,
      keptComponentTypes: section.keptComponentTypes,
      tailCodeByLabel
    })
  }));
};

export const listRankGroupSelections = (
  section: Pick<DuctedRailSection, 'system' | 'keptComponentTypes'>,
  group: DuctedRailRankGroup
): DuctedPanelSelection[] => [
  ...group.cards.flatMap((card) => {
    const selection = rankGroupCardSelection(section, card);
    return selection ? [selection] : [];
  }),
  ...rankGroupTailSelections(section, group).map(({ selection }) => selection)
];

export const isDuctedRailSectionSkipped = (
  section: Pick<DuctedRailSection, 'subSections'>
): boolean =>
  section.subSections[section.subSections.length - 1]?.entry?.status ===
  'skipped';

const settledGroupCandidates = (
  section: DuctedRailSection,
  group: DuctedRailRankGroup
): ComparedSelectionMatch[] => {
  const cards = group.cards.flatMap((card) => {
    const settled =
      card.entry.status === 'loaded' &&
      card.representative?.status === 'loaded';
    const selection = settled ? rankGroupCardSelection(section, card) : null;
    return selection ? [{ selection, expandKey: null }] : [];
  });
  const tails = rankGroupTailSelections(section, group)
    .filter(({ entry }) => entry.status === 'loaded')
    .map(({ selection }) => ({ selection, expandKey: group.key }));
  return [...cards, ...tails];
};

const sectionCandidates = (
  section: DuctedRailSection
): ComparedSelectionMatch[] =>
  isDuctedRailSectionSkipped(section)
    ? []
    : section.rankGroups.flatMap((group) =>
        settledGroupCandidates(section, group)
      );

export const listSettledSectionSelections = (
  section: DuctedRailSection
): DuctedPanelSelection[] =>
  sectionCandidates(section).map(({ selection }) => selection);

export const isDuctedRailSectionLoading = (
  section: Pick<DuctedRailSection, 'subSections'>
): boolean =>
  section.subSections.some(
    (subSection) =>
      subSection.entry?.status === 'loading' ||
      subSection.representativeEntries.some(
        ({ representative }) => representative?.status === 'loading'
      )
  );

const matchesComparedSystem = (
  selection: DuctedPanelSelection,
  comparedIdByCardId: ReadonlyMap<string, string>,
  system: DuctedComparedSystem
): boolean =>
  comparedIdByCardId.get(selection.cardId) === system.id ||
  findComparedSystemForCard([system], selection) !== undefined;

export const findSelectionForComparedSystem = (
  sections: ReadonlyArray<DuctedRailSection>,
  comparedIdByCardId: ReadonlyMap<string, string>,
  system: DuctedComparedSystem
): ComparedSelectionMatch | null =>
  sections
    .flatMap(sectionCandidates)
    .find(({ selection }) =>
      matchesComparedSystem(selection, comparedIdByCardId, system)
    ) ?? null;
